import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Check, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { WizardStepIndicator, WizardStepDef } from "@/components/ui/wizard-step-indicator";

import {
  MODE_META,
  SU_PLATFORM_META,
  SoftwareUpdateWizardState,
  defaultWizardState,
} from "./types";
import { isValidDeadline, isValidTargetVersion } from "./lib/buildConfig";
import { runSaveChain } from "./lib/saveChain";
import { ModePickerStep } from "./modes/ModePickerStep";
import { PinnedForm } from "./modes/PinnedForm";
import { RollingForm } from "./modes/RollingForm";
import { UpdateBehaviorForm } from "./modes/UpdateBehaviorForm";
import { ScopeStep } from "./steps/ScopeStep";
import { ReviewSaveStep } from "./steps/ReviewSaveStep";

type StepId = "mode" | "configure" | "scope" | "review";

const STEPS: WizardStepDef[] = [
  { id: "mode", name: "Mode", description: "Pick mode and platform" },
  { id: "configure", name: "Configure", description: "Mode-specific rules" },
  { id: "scope", name: "Scope", description: "Tags and targets" },
  { id: "review", name: "Review", description: "Confirm and save" },
];

interface SoftwareUpdateWizardProps {
  initial?: SoftwareUpdateWizardState;
  /** When provided, the wizard saves via update instead of create. */
  policyId?: string;
  returnTo?: string;
}

export function SoftwareUpdateWizard({
  initial,
  policyId,
  returnTo = "/software-updates?tab=policies",
}: SoftwareUpdateWizardProps) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [stepId, setStepId] = useState<StepId>("mode");
  const [completed, setCompleted] = useState<StepId[]>([]);
  const [state, setState] = useState<SoftwareUpdateWizardState>(
    () => initial ?? defaultWizardState(),
  );

  const stepIndex = STEPS.findIndex((s) => s.id === stepId);
  const isLast = stepId === "review";

  const update = (patch: Partial<SoftwareUpdateWizardState>) =>
    setState((s) => ({ ...s, ...patch }));

  const stepValid = useMemo(() => {
    if (stepId === "mode") return state.name.trim().length > 0;
    if (stepId === "configure") {
      if (state.mode === "pinned") {
        const { targetVersion, deadline } = state.pinned;
        return (
          isValidTargetVersion(targetVersion) &&
          deadline.length > 0 &&
          isValidDeadline(deadline)
        );
      }
      if (state.mode === "rolling") {
        if (state.rolling.lane === "specific-major" && !state.rolling.major.trim()) return false;
        return state.rolling.includeMinor || state.rolling.includeMajor;
      }
      return true; // Update Behavior — everything optional
    }
    return true;
  }, [stepId, state]);

  const goNext = () => {
    if (!stepValid) {
      toast.error("Complete the required fields before continuing.");
      return;
    }
    setCompleted((c) => (c.includes(stepId) ? c : [...c, stepId]));
    const next = STEPS[stepIndex + 1]?.id as StepId | undefined;
    if (next) setStepId(next);
  };

  const goBack = () => {
    if (stepIndex === 0) {
      navigate(returnTo);
      return;
    }
    setStepId(STEPS[stepIndex - 1].id as StepId);
  };

  const saveMutation = useMutation({
    mutationFn: () => runSaveChain(state, { policyId }),
    onSuccess: (res) => {
      if (res.kind === "created") {
        toast.success(
          res.scopeWarning ?? (policyId ? "Software update policy updated" : "Software update policy created"),
        );
        qc.invalidateQueries({ queryKey: ["policies"] });
        qc.invalidateQueries({ queryKey: ["policies", "software-updates"] });
        navigate(policyId ? returnTo : `/software-updates/policies/${res.policyId}`);
        return;
      }
      if (res.kind === "pending_approval") {
        toast.success(res.message);
        qc.invalidateQueries({ queryKey: ["policies"] });
        navigate(returnTo);
        return;
      }
      if (res.kind === "forbidden") {
        toast.error(res.message);
        return;
      }
      // invalid
      toast.error(res.errors[0] ?? "The configuration didn't pass validation.");
      setStepId("configure");
    },
    onError: (err: unknown) => {
      const msg =
        err instanceof Error
          ? err.message
          : "Couldn't reach the server. Check your connection and try again.";
      toast.error(msg);
    },
  });

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Sticky header */}
      <div className="sticky top-0 z-20 bg-background border-b">
        <div className="px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={goBack}
              disabled={saveMutation.isPending}
              aria-label={stepIndex === 0 ? "Exit wizard" : "Previous step"}
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            <div className="min-w-0">
              <h1 className="text-base font-semibold leading-tight flex items-center gap-2">
                <RefreshCw className="h-4 w-4 text-primary" aria-hidden="true" />
                {policyId ? "Edit software update policy" : "New software update policy"}
              </h1>
              <p className="text-xs text-muted-foreground leading-tight truncate">
                {MODE_META[state.mode].label} · {SU_PLATFORM_META[state.platform].label}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isLast ? (
              <Button onClick={goNext} disabled={saveMutation.isPending}>
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
                {saveMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                {policyId ? "Save changes" : "Create policy"}
              </Button>
            )}
          </div>
        </div>
        <WizardStepIndicator steps={STEPS} currentStep={stepId} completedSteps={completed} />
      </div>

      <ScrollArea className="flex-1">
        <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
          {stepId === "mode" && <ModePickerStep state={state} onChange={update} />}
          {stepId === "configure" && state.mode === "pinned" && (
            <PinnedForm state={state} onChange={(patch) => update({ pinned: { ...state.pinned, ...patch } })} />
          )}
          {stepId === "configure" && state.mode === "rolling" && (
            <RollingForm state={state} onChange={(patch) => update({ rolling: { ...state.rolling, ...patch } })} />
          )}
          {stepId === "configure" && state.mode === "update_behavior" && (
            <UpdateBehaviorForm
              state={state}
              onChange={(patch) => update({ behavior: { ...state.behavior, ...patch } })}
            />
          )}
          {stepId === "scope" && (
            <ScopeStep selectedTagIds={state.tagIds} onChange={(ids) => update({ tagIds: ids })} />
          )}
          {stepId === "review" && <ReviewSaveStep state={state} onJumpToStep={(id) => setStepId(id)} />}
        </div>
      </ScrollArea>
    </div>
  );
}
